import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthShell } from '@/components/auth/AuthShell';
import { AuthField } from '@/components/auth/AuthField';
import { NeonButton } from '@/components/ui/NeonButton';
import { Icon } from '@/components/ui/Icon';
import { useAuth } from '@/modules/auth/useAuth';
import { getSupabase } from '@/lib/supabase';
import { ROUTES } from '@/config/routes';

/**
 * Password recovery — step 2.
 *
 * Supabase delivers the recovery via a URL hash with `type=recovery` and
 * a session is auto-installed by `detectSessionInUrl`. We just need to
 * wait for that to settle, then let the user pick a new password.
 */
export const ResetPasswordPage = () => {
  const { setPassword, isMock } = useAuth();
  const navigate = useNavigate();
  const [sessionReady, setSessionReady] = useState(isMock);
  const [password, setPasswordValue] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isMock) return;
    const supabase = getSupabase();
    if (!supabase) return;
    let mounted = true;
    supabase.auth.getSession().then(({ data }) => {
      if (mounted && data.session) setSessionReady(true);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (!mounted) return;
      if (event === 'PASSWORD_RECOVERY' || (event === 'SIGNED_IN' && session)) {
        setSessionReady(true);
      }
    });
    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, [isMock]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password !== confirm) {
      setError('Passwords don’t match.');
      return;
    }
    setSubmitting(true);
    const result = await setPassword(password);
    setSubmitting(false);
    if (!result.ok) {
      setError(result.error ?? 'Could not update password.');
      return;
    }
    navigate(ROUTES.feed, { replace: true });
  };

  return (
    <AuthShell
      eyebrow="Set new password"
      title={
        <>
          New key<span className="text-pink">.</span>
        </>
      }
      intro={
        <>
          Pick something only you would type<span className="text-pink">.</span>
        </>
      }
      footer={
        <p className="text-center font-display text-[10px] font-bold uppercase tracking-widest text-text-muted">
          Trouble?{' '}
          <Link to={ROUTES.forgot} className="text-pink hover:underline">
            Request a new link
          </Link>
        </p>
      }
    >
      {!sessionReady ? (
        <p className="font-display text-[10px] font-bold uppercase tracking-widest text-text-muted">
          Verifying recovery link<span className="text-pink">.</span>
        </p>
      ) : (
        <form onSubmit={onSubmit} className="flex flex-col gap-5">
          <AuthField
            label="New password"
            type="password"
            value={password}
            onChange={(e) => setPasswordValue(e.target.value)}
            autoComplete="new-password"
            minLength={8}
            autoFocus
            required
          />
          <AuthField
            label="Confirm password"
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            minLength={8}
            required
          />
          {error && (
            <p className="font-display text-[10px] font-bold uppercase tracking-widest text-pink">
              — {error}
            </p>
          )}
          <NeonButton
            type="submit"
            size="md"
            disabled={submitting || password.length < 8 || password !== confirm}
            trailing={<Icon name="arrow_forward" size={14} />}
          >
            {submitting ? 'Saving' : 'Save new password'}
          </NeonButton>
        </form>
      )}
    </AuthShell>
  );
};
